import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Calculator, AlertTriangle, Check } from "lucide-react";
import styles from "./MarginEngine.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  useMarginEngine,
  useSaveMarginEngine,
  usePreviewMargin,
  MARGIN_PRODUCTS,
} from "../../../api/pricing/useMarginEngineApi";

/**
 * The central Margin Menu.
 *
 * One screen for all seven products, because SUNSKY's specification is explicit
 * that there is one engine rather than one per product. The left column is the
 * seven sections; the right is whichever one is open.
 *
 * ── the enable switch is the important control ──
 * A product that is OFF is not priced at zero margin: the engine declines it and
 * whatever priced that product before carries on untouched. That is what lets the
 * numbers be entered and checked without moving a live price, and it is why every
 * product ships disabled. Turning one on changes what customers are charged from
 * that moment, so the switch says so rather than being a quiet toggle.
 *
 * ── why there is a calculator on the page ──
 * A tier table is easy to get subtly wrong and the consequence is money. The
 * preview prices a hypothetical booking against the SAVED configuration, so the
 * person setting the numbers can check them before switching the product on.
 */

const num = (v) => (v === "" || v === null || v === undefined ? "" : v);
const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* ── small field components ─────────────────────────────────────────────── */

const Field = ({ label, suffix, value, onChange, hint, step = "0.01" }) => (
  <div className={styles.field}>
    <label>{label}</label>
    <div className={styles.inputWrap}>
      <input
        type="number"
        min="0"
        step={step}
        value={num(value)}
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix && <span className={styles.suffix}>{suffix}</span>}
    </div>
    {hint && <p className={styles.hint}>{hint}</p>}
  </div>
);

/**
 * The tier table for Hotel Only and Transfer Only.
 *
 * The server refuses a table with a gap, an overlap, no zero start or no
 * open-ended final tier, and says which. Rather than let somebody discover that on
 * save, the same checks run here as you type and show up as a warning line.
 */
const TierEditor = ({ tiers, onChange }) => {
  const rows = Array.isArray(tiers) ? tiers : [];

  const set = (i, key, v) => {
    const next = rows.map((t, j) => (j === i ? { ...t, [key]: v } : t));
    onChange(next);
  };

  const add = () => {
    const last = rows[rows.length - 1];
    const from = last && last.to != null ? Number(last.to) + 0.01 : 0;
    // The new row becomes the open-ended one; the previous last gets an upper bound.
    const next = rows.map((t, j) =>
      j === rows.length - 1 && (t.to === null || t.to === undefined)
        ? { ...t, to: Number(t.from) + 499.99 }
        : t
    );
    onChange([...next, { from: next.length ? Number(next[next.length - 1].to) + 0.01 : from, to: null, percent: 0 }]);
  };

  const remove = (i) => {
    const next = rows.filter((_, j) => j !== i);
    if (next.length) next[next.length - 1] = { ...next[next.length - 1], to: null };
    onChange(next);
  };

  const problems = useMemo(() => {
    const out = [];
    if (!rows.length) return ["Add at least one tier."];
    const sorted = [...rows].sort((a, b) => toNum(a.from) - toNum(b.from));
    const c = (n) => Math.round(toNum(n) * 100);
    if (c(sorted[0].from) !== 0) out.push("The first tier must start at 0.");
    const last = sorted[sorted.length - 1];
    if (last.to !== null && last.to !== undefined && last.to !== "") {
      out.push("The last tier must have no upper limit, so no booking is above the table.");
    }
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (a.to === null || a.to === undefined || a.to === "") {
        out.push("Only the last tier may have no upper limit.");
        break;
      }
      if (c(b.from) <= c(a.to)) out.push(`Tiers overlap around ${a.to}.`);
      else if (c(b.from) - c(a.to) > 1) out.push(`Gap between ${a.to} and ${b.from}: a booking there has no tier.`);
    }
    return out;
  }, [rows]);

  return (
    <div className={styles.tiers}>
      <div className={styles.tierHead}>
        <span>From (€)</span>
        <span>To (€)</span>
        <span>Target margin (%)</span>
        <span />
      </div>

      {rows.map((t, i) => {
        const openEnded = t.to === null || t.to === undefined || t.to === "";
        return (
          <div key={i} className={styles.tierRow}>
            <input type="number" min="0" step="0.01" value={num(t.from)} onChange={(e) => set(i, "from", e.target.value)} />
            <input
              type="number"
              min="0"
              step="0.01"
              value={openEnded ? "" : t.to}
              placeholder={openEnded ? "no limit" : ""}
              onChange={(e) => set(i, "to", e.target.value === "" ? null : e.target.value)}
            />
            <input type="number" min="0" step="0.01" value={num(t.percent)} onChange={(e) => set(i, "percent", e.target.value)} />
            <button type="button" className={styles.iconBtn} onClick={() => remove(i)} title="Remove this tier">
              <Trash2 size={14} />
            </button>
          </div>
        );
      })}

      <button type="button" className={styles.addTier} onClick={add}>
        <Plus size={14} /> Add tier
      </button>

      {problems.length > 0 && (
        <div className={styles.warn}>
          <AlertTriangle size={14} />
          <div>{problems.map((p, i) => <div key={i}>{p}</div>)}</div>
        </div>
      )}
    </div>
  );
};

/* ── the per-product field sets ─────────────────────────────────────────── */

const SectionFields = ({ productKey, section, patch }) => {
  switch (productKey) {
    case "flightOnly":
      return (
        <>
          <p className={styles.method}>
            A fixed amount per traveller, added once per booking. Never per segment, leg, PNR or
            airline: a return or multi-city trip takes the same margin as a one-way.
          </p>
          <div className={styles.grid3}>
            <Field label="Adult (ADT)" suffix="€" value={section.adt} onChange={(v) => patch({ adt: v })} />
            <Field label="Child (CHD)" suffix="€" value={section.chd} onChange={(v) => patch({ chd: v })} />
            <Field label="Infant (INF)" suffix="€" value={section.inf} onChange={(v) => patch({ inf: v })} />
          </div>
        </>
      );

    case "hotelOnly":
    case "transferOnly":
      return (
        <>
          <p className={styles.method}>
            The tier is chosen by the TOTAL supplier cost of the whole booking, not per room,
            night, traveller or route. The margin is then the higher of the tier percentage and
            the minimum below.
          </p>
          <TierEditor tiers={section.tiers} onChange={(tiers) => patch({ tiers })} />
          <div className={styles.grid3}>
            <Field
              label="Minimum target margin per booking"
              suffix="€"
              value={section.minMargin}
              onChange={(v) => patch({ minMargin: v })}
              hint="Applied when the tier percentage works out lower."
            />
          </div>
        </>
      );

    case "dynamicPackage":
      return (
        <>
          <p className={styles.method}>
            One margin over flight + hotel + optional transfer combined. The standalone Flight,
            Hotel and Transfer margins are never applied inside a package: that would charge the
            customer a margin on a margin.
          </p>
          <div className={styles.grid3}>
            <Field label="Base margin" suffix="€" value={section.baseMargin} onChange={(v) => patch({ baseMargin: v })} />
            <Field label="Variable margin" suffix="%" value={section.variablePercent} onChange={(v) => patch({ variablePercent: v })} />
            <Field label="Minimum target margin" suffix="€" value={section.minMargin} onChange={(v) => patch({ minMargin: v })} />
          </div>
          <p className={styles.formula}>
            Margin = MAX( minimum , base + total package supplier cost × variable % )
          </p>
        </>
      );

    case "cancellationInsurance":
      return (
        <>
          <p className={styles.method}>
            A percentage of the trip SELLING price before insurance, not of the supplier cost.
            One price for the booking, with no per-traveller rates.
          </p>
          <div className={styles.grid3}>
            <Field label="Cancellation insurance rate" suffix="%" value={section.ratePercent} onChange={(v) => patch({ ratePercent: v })} />
            <Field label="Minimum selling price" suffix="€" value={section.minSellingPrice} onChange={(v) => patch({ minSellingPrice: v })} />
          </div>
        </>
      );

    case "assistanceInsurance":
      return (
        <>
          <p className={styles.method}>
            Travellers × insured days × this rate. Insured days count BOTH the departure and
            return day, so 10 to 17 July is 8 days, not 7 nights. The same rate applies to every
            traveller type, with no geographic zones.
          </p>
          <div className={styles.grid3}>
            <Field
              label="Price per person per day"
              suffix="€"
              value={section.pricePerPersonPerDay}
              onChange={(v) => patch({ pricePerPersonPerDay: v })}
            />
          </div>
        </>
      );

    case "carRentalOnly":
      return (
        <>
          <p className={styles.method}>
            Applied once to the total car rental supplier cost. The rental duration is already
            reflected in that cost, so it is not priced per day or per driver.
          </p>
          <div className={styles.grid3}>
            <Field label="Car rental margin" suffix="%" value={section.marginPercent} onChange={(v) => patch({ marginPercent: v })} />
            <Field label="Minimum target margin" suffix="€" value={section.minMargin} onChange={(v) => patch({ minMargin: v })} />
          </div>
        </>
      );

    default:
      return null;
  }
};

/* ── the preview calculator ─────────────────────────────────────────────── */

const PREVIEW_FIELDS = {
  FLIGHT_ONLY: [
    { key: "adults", label: "Adults", def: 2 },
    { key: "children", label: "Children", def: 1 },
    { key: "infants", label: "Infants", def: 0 },
    { key: "supplierCost", label: "Supplier cost (€)", def: 850 },
  ],
  HOTEL_ONLY: [{ key: "supplierCost", label: "Total supplier cost (€)", def: 1750 }],
  TRANSFER_ONLY: [{ key: "supplierCost", label: "Total supplier cost (€)", def: 250 }],
  DYNAMIC_PACKAGE: [
    { key: "flightCost", label: "Flight supplier cost (€)", def: 1000 },
    { key: "hotelCost", label: "Hotel supplier cost (€)", def: 1000 },
    { key: "transferCost", label: "Transfer supplier cost (€)", def: 0 },
  ],
  CANCELLATION_INSURANCE: [
    { key: "tripSellingPriceBeforeInsurance", label: "Trip selling price before insurance (€)", def: 2000 },
  ],
  ASSISTANCE_INSURANCE: [
    { key: "travellers", label: "Insured travellers", def: 4 },
    { key: "days", label: "Insured days", def: 8 },
  ],
  CAR_RENTAL_ONLY: [{ key: "supplierCost", label: "Total supplier cost (€)", def: 500 }],
};

const Preview = ({ product }) => {
  const { preview, previewing } = usePreviewMargin();
  const fields = PREVIEW_FIELDS[product] || [];
  const [input, setInput] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    setInput(Object.fromEntries(fields.map((f) => [f.key, f.def])));
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const run = async () => setResult(await preview(product, input));

  return (
    <div className={styles.preview}>
      <div className={styles.previewHead}>
        <Calculator size={14} /> Check a booking against the saved settings
      </div>
      <div className={styles.previewGrid}>
        {fields.map((f) => (
          <div key={f.key} className={styles.field}>
            <label>{f.label}</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={num(input[f.key])}
              onChange={(e) => setInput((s) => ({ ...s, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <button type="button" className={styles.secondaryBtn} onClick={run} disabled={previewing}>
        {previewing ? "Calculating..." : "Calculate"}
      </button>

      {result && (
        <div className={result.ok ? styles.resultOk : styles.resultBad}>
          {result.ok ? (
            <>
              <strong>
                {result.margin !== undefined
                  ? `Margin €${result.margin}`
                  : `Selling price €${result.sellingPrice}`}
              </strong>
              {result.sellingPrice !== undefined && result.margin !== undefined && (
                <span> · customer pays €{result.sellingPrice}</span>
              )}
              <span className={styles.basis}> · {result.basis}</span>
            </>
          ) : (
            <>
              <AlertTriangle size={13} />{" "}
              {result.reason === "PRODUCT_DISABLED"
                ? "This product is switched off, so the engine is not pricing it yet."
                : result.reason === "NO_TIER_FOR_COST"
                ? "No tier covers that amount. Check the tier table for a gap."
                : `Not priced: ${result.reason}`}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ── the screen ─────────────────────────────────────────────────────────── */

export default function MarginEngine() {
  const { showToast } = useToast();
  const { config, setConfig, loading, error, refetch } = useMarginEngine();
  const { save, saving } = useSaveMarginEngine();
  const [active, setActive] = useState("flightOnly");

  const meta = MARGIN_PRODUCTS.find((p) => p.key === active);
  const section = config?.[active];

  const patch = (fields) =>
    setConfig((c) => ({ ...c, [active]: { ...c[active], ...fields } }));

  const onSave = async () => {
    try {
      // Numbers arrive from the inputs as strings; the API takes numbers.
      const clean = { ...section };
      for (const k of Object.keys(clean)) {
        if (k === "enabled") continue;
        if (k === "tiers") {
          clean.tiers = (clean.tiers || []).map((t) => ({
            from: toNum(t.from),
            to: t.to === null || t.to === undefined || t.to === "" ? null : toNum(t.to),
            percent: toNum(t.percent),
          }));
          continue;
        }
        clean[k] = toNum(clean[k]);
      }
      const saved = await save({ [active]: clean });
      showToast(
        clean.enabled
          ? `${meta.label} saved and LIVE. Customer prices now use these values.`
          : `${meta.label} saved. It stays switched off, so no customer price changed.`,
        "success"
      );
      if (saved?.marginEngine) setConfig(saved.marginEngine);
      else await refetch();
    } catch (e) {
      showToast(e.message, "error");
    }
  };

  if (loading) return <div className={styles.page}><div className={styles.loading}>Loading the margin settings...</div></div>;
  if (error) return <div className={styles.page}><div className={styles.warn}><AlertTriangle size={14} /> {error}</div></div>;
  if (!config || !section) return null;

  const liveCount = MARGIN_PRODUCTS.filter((p) => config[p.key]?.enabled).length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Central Margin Engine</h1>
          <p>
            One place for the commercial margin on every product SUNSKY sells. Each product uses
            its own approved method; the values here are never hardcoded in the software.
          </p>
        </div>
        <div className={styles.liveCount}>
          {liveCount === 0
            ? "No product is live yet"
            : `${liveCount} of ${MARGIN_PRODUCTS.length} products live`}
        </div>
      </div>

      <div className={styles.body}>
        <nav className={styles.nav}>
          {MARGIN_PRODUCTS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`${styles.navItem} ${active === p.key ? styles.navItemOn : ""}`}
              onClick={() => setActive(p.key)}
            >
              <span className={styles.navLabel}>{p.label}</span>
              <span className={styles.navMethod}>{p.method}</span>
              <span className={config[p.key]?.enabled ? styles.dotOn : styles.dotOff}>
                {config[p.key]?.enabled ? "Live" : "Off"}
              </span>
            </button>
          ))}
        </nav>

        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <div>
              <h2>{meta.label}</h2>
              <span className={styles.methodTag}>{meta.method}</span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={!!section.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
              <span className={styles.slider} />
              <span className={styles.switchLabel}>
                {section.enabled ? "Live" : "Switched off"}
              </span>
            </label>
          </div>

          {!section.enabled && (
            <div className={styles.offNote}>
              While this is off, the engine does not price {meta.label}. Whatever prices it today
              carries on unchanged, so you can set and check the numbers safely first.
            </div>
          )}

          <SectionFields productKey={active} section={section} patch={patch} />

          <Preview product={meta.product} />

          <div className={styles.actions}>
            <button type="button" className={styles.primaryBtn} onClick={onSave} disabled={saving}>
              {saving ? "Saving..." : <><Save size={14} /> Save {meta.label}</>}
            </button>
            {section.enabled && (
              <span className={styles.liveWarn}>
                <Check size={13} /> Saving with this switched on changes customer prices immediately.
              </span>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
